import { useCallback, useEffect, useState } from 'react';
import { Share } from '@capacitor/share';
import {
  Box,
  Button,
  Card,
  Icon,
  Skeleton,
  Spinner,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { PrinterIcon } from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { WorkerMessageHandler } from 'pdfjs-dist/build/pdf.worker.min.mjs';
import * as ReactPDF from 'react-pdf';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import { trackingNuvemEnvioPrintDocumentsPageButtonConfirm } from 'domains/NuvemEnvioCommons/tracking';
import { useOrderDetails } from 'domains/Orders/Orders/hooks';
import useCreateNuvemEnvioLabels from '../../hooks/useCreateNuvemEnvioLabels/useCreateNuvemEnvioLabels';
import { useGoBackFromLabelPrinting } from '../../hooks/useGoBackFromLabelPrinting';
import { useLabelPrintingQuery } from '../../hooks/useLabelPrintingQuery';
import { usePrintNuvemEnvioLabelToast } from '../../hooks/usePrintNuvemEnvioLabelToast';
import './PrintDocumentsPage.scss';

ReactPDF.pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  WorkerMessageHandler,
  import.meta.url,
).toString();

function OrdersPrintDocumentsPage(): JSX.Element {
  const t = useNuvemEnvioTranslations();
  const isMobile = useIsMobileDevice();
  const { orderId, fulfillmentOrderIds, source } = useLabelPrintingQuery();
  const {
    orderDetails,
    isLoading: isLoadingOrderDetails,
    isError: errorOnGetOrderDetails,
  } = useOrderDetails(orderId);
  const { goBack } = useGoBackFromLabelPrinting({ orderId, source });
  const { createNuvemEnvioLabels, status, data, resetNuvemEnvioLabels } =
    useCreateNuvemEnvioLabels();
  const { addErrorToast } = usePrintNuvemEnvioLabelToast();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    if (isPrinting) return;
    setIsPrinting(true);

    try {
      trackingNuvemEnvioPrintDocumentsPageButtonConfirm(
        fulfillmentOrderIds.length.toString(),
        fulfillmentOrderIds.length > 1,
        'confirm',
      );
      const response = await createNuvemEnvioLabels({
        createFile: { label: true, contentDeclaration: false },
        fulfillmentOrderIds,
      });

      if (!response.labelUrls.length) {
        throw new Error('Unexpected error creating labels');
      }

      if (isMobile) {
        setIsPrinting(false);
        await Share.share({ url: response.labelUrls[0] });
      }
    } catch (error) {
      if (error?.message === 'Share canceled') return;
      addErrorToast();
      setIsPrinting(false);
    }
  }, [
    isPrinting,
    addErrorToast,
    createNuvemEnvioLabels,
    isMobile,
    fulfillmentOrderIds,
  ]);

  const handleCancel = useCallback(() => {
    trackingNuvemEnvioPrintDocumentsPageButtonConfirm(orderId, false, 'cancel');

    goBack();
  }, [orderId, goBack]);

  const onPdfRenderSuccess = () => {
    globalThis.print();
    setIsPrinting(false);
  };

  const onPdfRenderError = () => {
    addErrorToast();
    setIsPrinting(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        handlePrint();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
      resetNuvemEnvioLabels();
    };
  }, [handlePrint, resetNuvemEnvioLabels]);

  useEffect(() => {
    if (errorOnGetOrderDetails) {
      addErrorToast();
      goBack();
    }
  }, [errorOnGetOrderDetails, goBack, addErrorToast]);

  return (
    <Page id="NuvemEnvioPrintDocumentsPage">
      <Page.Header title={t('printPageDocument.title')} id="header" />
      <Page.Body>
        <Box width={{ xs: '100%', md: '50%' }} id="area">
          {!orderDetails && isLoadingOrderDetails ? (
            <Card>
              <Card.Body>
                <Skeleton width="100%" height="200px" />
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="4">
                  <Box
                    width="60%"
                    display="flex"
                    flexDirection="column"
                    gap="2"
                  >
                    <Text color="neutral-textLow">
                      {t('printPageDocument.labels.selectedOrders')}
                    </Text>
                    <Tag appearance="neutral">#{orderDetails?.number}</Tag>
                  </Box>

                  <Box width="100%" display="flex" gap="4">
                    <Button onClick={handleCancel}>
                      {t('printPageDocument.buttons.secondary')}
                    </Button>
                    <Button appearance="primary" onClick={handlePrint}>
                      {isPrinting ? (
                        <Spinner size="small" color="currentColor" />
                      ) : (
                        <Icon source={<PrinterIcon />} color="currentColor" />
                      )}
                      {t('printPageDocument.buttons.primary')}
                    </Button>
                  </Box>
                </Box>
              </Card.Body>
            </Card>
          )}
        </Box>

        <div className="pdf-print">
          {!isMobile && status.isSuccess && (
            <ReactPDF.Document file={data.labelUrls[0]}>
              <ReactPDF.Page
                pageNumber={1}
                scale={2.5}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onRenderSuccess={onPdfRenderSuccess}
                onRenderError={onPdfRenderError}
              />
            </ReactPDF.Document>
          )}
        </div>
      </Page.Body>
    </Page>
  );
}

export default OrdersPrintDocumentsPage;
